import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import type { AuthResponse } from '@/features/auth/types';

const baseURL = import.meta.env.VITE_API_URL ?? '/api';

/**
 * Instance Axios principale.
 * `withCredentials` : envoi du cookie httpOnly de refresh token.
 */
export const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ------------------------------------------------------------
//  Access token en mémoire (jamais en localStorage → anti-XSS)
// ------------------------------------------------------------
let accessToken: string | null = null;
export const setAccessToken = (token: string | null) => {
  accessToken = token;
};
export const getAccessToken = () => accessToken;

// Callback appelé lorsque la session ne peut pas être restaurée (refresh KO).
let onAuthFailure: (() => void) | null = null;
export const setAuthFailureHandler = (fn: (() => void) | null) => {
  onAuthFailure = fn;
};

// ------------------------------------------------------------
//  Rafraîchissement (mono-vol : une seule requête concurrente)
// ------------------------------------------------------------
let refreshPromise: Promise<AuthResponse> | null = null;

/** Appelle /auth/refresh via une instance nue (évite la récursion d'intercepteur). */
export async function requestRefresh(): Promise<AuthResponse> {
  if (!refreshPromise) {
    refreshPromise = axios
      .post<AuthResponse>(`${baseURL}/auth/refresh`, null, { withCredentials: true })
      .then((res) => {
        setAccessToken(res.data.accessToken);
        return res.data;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Attache le token d'accès à chaque requête.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Sur 401, tente un refresh unique puis rejoue la requête d'origine.
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = original?.url ?? '';
    const isAuthRoute = url.includes('/auth/login') || url.includes('/auth/refresh');

    if (error.response?.status === 401 && original && !original._retry && !isAuthRoute) {
      original._retry = true;
      try {
        const { accessToken: newToken } = await requestRefresh();
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        setAccessToken(null);
        onAuthFailure?.();
      }
    }
    return Promise.reject(error);
  },
);
